import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import { BASE_URL, AUTH_CONFIG } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(AUTH_CONFIG.tokenKey);
      
      if (token) {
        try {
          // Check if token is expired
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          
          if (decoded.exp < currentTime) {
            // Token expired, log out
            logout();
          } else {
            // Token valid, fetch user data
            await getUserData(token);
          }
        } catch (error) {
          console.error('Token validation error:', error);
          logout();
        }
      }
      
      setLoading(false);
    };
    
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserData = async (token) => {
    try {
      const response = await fetch(`${BASE_URL}/auth/me`, {
        headers: {
          [AUTH_CONFIG.apiKeyHeader]: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setApiKey(data.api_key);
        setIsAuthenticated(true);
      } else {
        // Token invalid
        logout();
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      logout();
    }
  };

  const login = async (googleResponse) => {
    try {
      // First step in the process: get the callback response with the user and API key
      localStorage.setItem(AUTH_CONFIG.tokenKey, googleResponse.access_token);
      setUser(googleResponse.user);
      setApiKey(googleResponse.api_key);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const generateApiKey = async () => {
    try {
      const token = localStorage.getItem(AUTH_CONFIG.tokenKey);
      const response = await fetch(`${BASE_URL}/auth/api-key`, {
        method: 'POST',
        headers: {
          [AUTH_CONFIG.apiKeyHeader]: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const newApiKey = await response.json();
        setApiKey(newApiKey);
        return newApiKey;
      }
      return null;
    } catch (error) {
      console.error('Error generating API key:', error);
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_CONFIG.tokenKey);
    setUser(null);
    setApiKey(null);
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    user,
    apiKey,
    loading,
    login,
    logout,
    generateApiKey
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 